import { useEffect, useRef, useState } from 'preact/hooks';
import { SKILLS, KEYSTONES, HEROES, PETS, BLESSINGS, CROWD_EFFECTS, CROWD_IDS, RANK_MAX, BACKPACK_SLOTS, aspectPath, resolveSkill, type DraftOption, type HeroId, type SkillView } from '@knight/shared';
import * as S from '../app/state';
import { t, tn, td } from '../i18n';
import { Btn, Icon, EmptySlot, copyText, origin, fmtTime, HeroPortrait, skillTip, skillDesc, gainLines, keystoneTip, consumableTip, itemTip, blessingTip, crowdTip } from './common';
import { KitPanel, GearPanel, groupBlessings } from './Kit';
import { game } from '../game/client';
import { KEY_LABELS } from '../game/input';
import { api } from '../net/api';
import { unlockedView } from '../app/heroes';
import { isTouchUi } from '../app/device';
import { TouchControls } from './Touch';

export function GameScreen() {
  useEffect(() => {
    const el = document.getElementById('game')!;
    void game.mountView(el);
    return () => game.unmountView();
  }, []);
  const spec = S.spectate.value;
  const touch = isTouchUi();
  const overlay = S.menuOpen.value || S.buildOpen.value || S.chatOpen.value || !!S.deathCard.value;
  // a tablet with a mouse plugged in flips the pointer type mid-session
  useEffect(() => game.applyControlMode(), [touch]);
  // a panel opening under a resting thumb must not leave the hero walking
  useEffect(() => {
    if (overlay) game.input.clearSticks();
  }, [overlay]);
  return (
    <div class={'hud' + (touch ? ' touch' : '')}>
      <Hud />
      {touch && <TouchControls />}
      {spec && <SpectatorBar />}
      {spec && <CrowdPanel />}
      {!spec && S.self.value?.draft && !S.self.value.dead && <Draft />}
      <ExitCast />
      {S.buildOpen.value && <BuildPanel />}
      <Feed />
      <Chat />
      {S.announce.value && (
        <div class="announce" key={S.announce.value.at} style={{ color: S.announce.value.color ?? '#fff' }}>
          {S.announce.value.text}
        </div>
      )}
      {S.deathCard.value && !spec && <DeathCard />}
      {S.gameMode.value === 'duel' && S.duel.value?.phase === 'end' && <DuelEnd />}
      {S.menuOpen.value && <Menu />}
    </div>
  );
}

function DuelEnd() {
  const d = S.duel.value!;
  const myName = S.account.value?.name;
  const won = !!d.winner && d.winner === myName;
  return (
    <div class="modal">
      <div class="card narrow panel col center">
        <h1 style={{ fontSize: 34, color: won ? 'var(--gold)' : 'var(--blood)' }}>{won ? t('duel.won') : t('duel.lost')}</h1>
        <div class="muted">{d.winner ? t('duel.victory', { name: d.winner }) : t('duel.end')}</div>
        <div style={{ fontSize: 22, fontFamily: 'var(--serif)' }}>{d.names.map((n, i) => `${n} ${d.wins[i] ?? 0}`).join('  :  ')}</div>
        <div class="row" style={{ justifyContent: 'center' }}>
          <Btn primary onClick={() => game.leave()}>
            {t('btn.menu')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Hud() {
  const me = S.self.value;
  const realm = S.realm.value;
  const duel = S.duel.value;
  const boss = S.bossBar.value;
  const spec = S.spectate.value;
  if (!me) return null;
  const hpPct = Math.min(100, (me.hp / Math.max(1, me.mhp)) * 100);
  const shPct = Math.min(100, (me.sh / Math.max(1, me.mhp)) * 100);
  const xpPct = Math.min(100, (me.xp / Math.max(1, me.xpNext)) * 100);
  const rankPct = me.rank >= RANK_MAX ? 100 : Math.min(100, (me.rankXp / Math.max(1, me.rankXpNext)) * 100);
  return (
    <>
      <div class="tl plate panel">
        <HeroPortrait hero={me.hero} size={58} />
        <div class="col grow" style={{ gap: 3 }}>
          <div class="row" style={{ gap: 6 }}>
            <b class="pname" style={{ color: HEROES[me.hero]?.accent ?? 'var(--gold)' }}>
              {spec ? spec.name : S.account.value?.name ?? tn('hero', me.hero)}
            </b>
            <span class="lvpill" title={t('label.levelBar')}>
              {t('label.lvl')} {me.level}
            </span>
            <span class="rkpill" title={t('label.rankBar')}>
              {t('label.rankShort')} {me.rank}
            </span>
            {me.pts > 0 && !spec && (
              <span class="pts clickable" onClick={() => (S.buildOpen.value = true)} title={t('label.pointsHint')}>
                +{me.pts}
              </span>
            )}
            <div class="grow" />
            <span class="small muted" title={t('label.kills')}>
              ☠ {me.kills}
            </span>
            {me.pet && (
              <span class="small muted" title={`${tn('pet', me.pet.id)} · ${t('label.lvl')} ${me.pet.level}`}>
                {PETS[me.pet.id]?.glyph ?? '🐾'}
              </span>
            )}
          </div>
          <div class={'bar hp' + (hpPct < 30 ? ' low' : '')}>
            <div class="fill hp" style={{ width: hpPct + '%' }} />
            {me.sh > 0 && <div class="fill sh" style={{ width: shPct + '%', opacity: 0.6 }} />}
            <div class="txt">
              {me.hp} / {me.mhp} {me.sh > 0 ? `(+${me.sh})` : ''}
            </div>
          </div>
          <div class="row" style={{ gap: 4 }}>
            <div class="bar thin grow" title={t('label.levelBar')}>
              <div class="fill xp" style={{ width: xpPct + '%' }} />
            </div>
            <div class="bar thin rank grow" title={t('label.rankBar')}>
              <div class="fill rk" style={{ width: rankPct + '%' }} />
            </div>
          </div>
        </div>
      </div>
      {boss && (
        <div class="bossbar col" style={{ gap: 2 }}>
          <div class="center small" style={{ textShadow: '0 1px 2px #000' }}>
            <b style={{ color: '#ff8080', fontFamily: 'var(--serif)', fontSize: 15 }}>{boss.name}</b>
          </div>
          <div class="bar">
            <div class="fill boss" style={{ width: Math.min(100, (boss.hp / Math.max(1, boss.mhp)) * 100) + '%' }} />
            <div class="txt">
              {boss.hp} / {boss.mhp}
            </div>
          </div>
        </div>
      )}
      {S.gameMode.value === 'realm' && realm && !spec && (
        <div class="tr col panel" style={{ gap: 4, background: 'rgba(0,0,0,0.35)', padding: 8, borderRadius: 4 }}>
          <div class="row small">
            <span class="muted">{t('label.realm')}</span>
            <b style={{ letterSpacing: '.1em' }}>{realm.code}</b>
            <div class="grow" />
            <span class="muted">
              {t('label.players')}: {realm.players}
            </span>
          </div>
          <div class="bar thin" title={t('label.bossApproaches')}>
            <div class="fill cor" style={{ width: Math.round(realm.corruption * 100) + '%' }} />
          </div>
          <div class="row small muted">
            <span>{t('label.corruption')}</span>
            <div class="grow" />
            <span>{Math.round(realm.corruption * 100)}%</span>
          </div>
          <div class={'objective small' + (realm.phase === 'boss' ? ' boss' : '')}>
            {realm.phase === 'boss' ? t('obj.boss', { boss: tn('mob', realm.boss) }) : realm.phase === 'closing' ? t('obj.closing', { s: realm.closeIn }) : realm.phase === 'closed' ? '' : t('obj.open')}
          </div>
          <div class="row">
            {!isTouchUi() && (
              <Btn
                small
                onClick={() => {
                  const url = `${origin()}#/r/${realm.code}${S.account.value ? `?ref=${S.account.value.id}` : ''}`;
                  void copyText(url);
                  S.showToast(t('toast.copied'));
                  void api.event('share', { kind: 'realm' });
                }}
              >
                {t('btn.invite')}
              </Btn>
            )}
            <Btn small danger title={t('label.exitHint')} onClick={() => game.toggleLeave()}>
              {S.leaving.value ? t('btn.cancel') : isTouchUi() ? t('btn.exit') : t('btn.exitRealm', { key: KEY_LABELS.leave })}
            </Btn>
            {S.conn.value === 'online' && S.account.value && !isTouchUi() && (
              <Btn
                small
                title={t('label.showLinkHint')}
                onClick={() => {
                  void copyText(`${origin()}#/w/${encodeURIComponent(S.account.value!.name)}`);
                  S.showToast(t('toast.copied'));
                  void api.event('share', { kind: 'watch' });
                }}
              >
                {t('btn.showLink')}
              </Btn>
            )}
          </div>
        </div>
      )}
      {S.gameMode.value === 'duel' && duel && (
        <div class="tr col panel" style={{ gap: 4, background: 'rgba(0,0,0,0.35)', padding: 8, borderRadius: 4 }}>
          <b class="gold">{t('duel.' + (duel.phase === 'waiting' ? 'waiting' : duel.phase === 'draft' ? 'draft' : duel.phase === 'countdown' ? 'countdown' : duel.phase === 'end' ? 'end' : 'fight'))}</b>
          <div class="small">
            {t('label.round', { n: duel.round })} · {duel.names.map((n, i) => `${n}: ${duel.wins[i] ?? 0}`).join(' — ')}
          </div>
          {duel.until > 0 && <div class="small muted">{fmtTime(duel.until)}</div>}
          {duel.winner && <div class="gold">{t('duel.victory', { name: duel.winner })}</div>}
        </div>
      )}
      <div class="statuses" style={spec ? { top: 60, right: 260 } : undefined}>
        {me.st.map((s) => (
          <div class="status chip" style={{ borderColor: statusColor(s.id) }}>
            <span class="dot" style={{ background: statusColor(s.id) }} />
            <span>
              {tn('status', s.id)}
              {s.stacks > 1 ? ` ×${s.stacks}` : ''}
            </span>
            <span class="muted">{Math.ceil(s.left)}</span>
          </div>
        ))}
      </div>
      <div class="bottom">
        <div class="slots">
          {[0, 1, 2, 3].map((i) => {
            const s = me.actives[i];
            if (!s) return <EmptySlot keyLabel={isTouchUi() ? undefined : KEY_LABELS.skills[i]} />;
            return (
              <div class="slotwrap">
                <Icon kind="skill" id={s.id} level={s.level} cd={s.cd} cdText={cdSeconds(s)} keyLabel={spec || isTouchUi() ? undefined : KEY_LABELS.skills[i]} tip={skillTip(s.id, s.level, s.aspects)} onClick={spec ? undefined : () => game.input.pressSkill(i)} />
                {s.auto && <span class="autotag" title={t('label.autoCast')}>A</span>}
              </div>
            );
          })}
          <div style={{ width: 14 }} />
          <div class="col" style={{ gap: 2, alignItems: 'center' }}>
            <div class="row" style={{ gap: 3 }}>
              {Array.from({ length: me.dash.max }).map((_, i) => (
                <div style={{ width: 10, height: 10, borderRadius: 5, background: i < me.dash.charges ? '#7fffd4' : 'rgba(255,255,255,0.15)', border: '1px solid #000' }} />
              ))}
            </div>
            {!isTouchUi() && <span class="small muted">{KEY_LABELS.dash}</span>}
          </div>
          <div style={{ width: 14 }} />
          {[0, 1, 2, 3].map((i) => {
            const c = me.cons[i];
            if (!c) return <EmptySlot keyLabel={isTouchUi() ? undefined : KEY_LABELS.cons[i]} size="sm" />;
            return <Icon kind="cons" id={c.id} size="sm" count={c.n} keyLabel={spec || isTouchUi() ? undefined : KEY_LABELS.cons[i]} tip={consumableTip(c.id, c.n)} onClick={spec ? undefined : () => game.input.pressUse(i)} />;
          })}
        </div>
        <div class="row" style={{ gap: 4 }}>
          {me.autos.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" level={s.level} tip={skillTip(s.id, s.level, s.aspects)} />
          ))}
          {me.traits.map((tr) => (
            <Icon kind="skill" id={tr.id} size="sm" level={tr.level} tip={skillTip(tr.id, tr.level)} dim />
          ))}
          {me.keystones.map((k) => (
            <Icon kind="keystone" id={k} size="sm" tip={keystoneTip(k)} />
          ))}
          {groupBlessings(me.bless).map(([id, n]) => (
            <Icon kind="blessing" id={id} size="sm" count={n > 1 ? n : undefined} tip={blessingTip(id, n)} />
          ))}
          <span style={{ width: 8 }} />
          {[me.eq.weapon, me.eq.armor, me.eq.charm].map((it) => (it ? <Icon kind="item" id={it.def} size="sm" tip={itemTip(it)} /> : null))}
          {me.bp.length > 0 && (
            <span class="small muted clickable" style={{ textShadow: '0 1px 2px #000' }} onClick={() => (S.buildOpen.value = true)}>
              🎒 {me.bp.length}/{BACKPACK_SLOTS}
            </span>
          )}
        </div>
      </div>
      {me.dead && !S.deathCard.value && S.gameMode.value !== 'duel' && !spec && <div class="hint">{t('label.spectating')}</div>}
      {me.dead && spec && <div class="hint">{t('label.targetDead')}</div>}
      <div class="pingbox">{S.ping.value} ms</div>
    </>
  );
}

/** Seconds left on a skill, from the cooldown fraction the server sends. */
function cdSeconds(s: SkillView): string | undefined {
  if (s.cd <= 0) return undefined;
  const def = SKILLS[s.id];
  if (!def) return undefined;
  const total = (def.cooldown ?? 5) * resolveSkill(def, s.aspects).cooldownMul;
  const left = s.cd * total;
  return left >= 10 ? String(Math.ceil(left)) : (Math.ceil(left * 10) / 10).toFixed(1);
}

function statusColor(id: string): string {
  const m: Record<string, string> = { burn: '#ff7a2f', chill: '#7fdcff', shock: '#a8f0ff', blight: '#8fd66a', bleed: '#ff3b5c', vulnerable: '#c65cff', weak: '#8a6a9a', haste: '#7fffd4', might: '#ffb35c', fortify: '#d9c9a3', regen: '#7be0a0', shield: '#dfe9ff', taunt: '#ff9a3c', stun: '#ffe38a', invuln: '#fff', tiger: '#ffb347', bear: '#b07a4a', attune_fire: '#ff7a2f', attune_frost: '#7fdcff', attune_storm: '#a8f0ff', inspire: '#ffd166' };
  return m[id] ?? '#fff';
}

export function skillTitle(id: string, level: number, aspects?: string[]): string {
  const def = SKILLS[id];
  let s = `${tn('skill', id)} ${t('label.lvl')} ${level}\n${td('skill', id).replace('{dur}', String(14 + (level - 1)))}`;
  for (const a of aspectPath(id, aspects ?? [])) s += `\n◆ ${tn('aspect', a.id)} — ${td('aspect', a.id)}`;
  if (def?.tags.length) s += `\n[${def.tags.join(', ')}]`;
  return s;
}

// ------------------------------------------------------------------ show mode: spectator UI

function SpectatorBar() {
  const spec = S.spectate.value!;
  const me = S.self.value;
  return (
    <div class="specbar panel">
      <span class="eye">👁</span>
      <HeroPortrait hero={spec.heroId} size={28} />
      <div class="col" style={{ gap: 0 }}>
        <b style={{ color: HEROES[spec.heroId]?.accent ?? 'var(--gold)' }}>{t('label.watchingName', { name: spec.name })}</b>
        <span class="small muted">
          {tn('hero', spec.heroId)}
          {me ? ` · ${t('label.lvl')} ${me.level} · ${t('label.rankShort')} ${me.rank}` : ''}
          {S.realm.value ? ` · ${t('label.realm')} ${S.realm.value.code}` : ''}
        </span>
      </div>
      <Btn small onClick={() => (S.buildOpen.value = !S.buildOpen.value)}>
        {t('label.build')}
      </Btn>
      <Btn small danger onClick={() => game.unwatch()}>
        {t('btn.stopWatching')}
      </Btn>
    </div>
  );
}

function CrowdPanel() {
  const spec = S.spectate.value!;
  const acc = S.account.value;
  const me = S.self.value;
  const prices = S.crowdPrices.value;
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, []);
  const cdLeft = Math.max(0, 15000 - (performance.now() - S.crowdLastBuy.value));
  const fame = acc?.fame ?? 0;
  const priceOf = (id: string) => prices?.[id] ?? CROWD_EFFECTS[id].cost;
  const group = (kind: 'help' | 'harm') => CROWD_IDS.filter((id) => CROWD_EFFECTS[id].kind === kind);
  const dead = !me || me.dead;
  return (
    <div class="crowd panel col">
      <div class="row">
        <b class="gold">{t('label.crowdTitle')}</b>
        <div class="grow" />
        <span class="small muted">
          {t('label.fame')} {fame}
        </span>
      </div>
      {!spec.showMode ? (
        <div class="small muted">{t('label.crowdOff', { name: spec.name })}</div>
      ) : (
        <>
          <div class="small muted">{t('label.crowdHint')}</div>
          {(['help', 'harm'] as const).map((kind) => (
            <div class="col" style={{ gap: 4 }}>
              <span class={'small ' + (kind === 'harm' ? 'blood' : 'green')}>{t(kind === 'harm' ? 'label.crowdHarm' : 'label.crowdHelp')}</span>
              <div class="crowd-grid">
                {group(kind).map((id) => {
                  const price = priceOf(id);
                  const can = !dead && cdLeft <= 0 && fame >= price;
                  return (
                    <div class={'crowd-btn' + (can ? '' : ' off')} onClick={() => can && game.crowd(id)}>
                      <Icon kind="crowd" id={id} size="sm" tip={crowdTip(id, price)} />
                      <span class="small">{tn('crowd', id)}</span>
                      <span class={'small ' + (fame >= price ? 'gold' : 'blood')}>{price}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div class="small muted">{cdLeft > 0 ? t('label.crowdCooldown', { s: Math.ceil(cdLeft / 1000) }) : dead ? t('label.targetDead') : t('label.crowdReady')}</div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ level-up cards

function Draft() {
  const me = S.self.value!;
  const d = me.draft!;
  const secs = Math.max(0, Math.round((d.until - (game.view?.serverTick ?? 0)) / 20));
  const title = d.kind === 'keystone' ? t('label.keystonePick') : t('label.draftPick', { n: me.level });
  return (
    <div class="draft">
      <div style={{ fontFamily: 'var(--serif)', fontSize: 18, textShadow: '0 1px 3px #000' }}>{title}</div>
      <div class="cards">
        {d.options.map((o, i) => (
          <DraftCard opt={o} idx={i} />
        ))}
      </div>
      <div class="small muted" style={{ textShadow: '0 1px 2px #000' }}>
        {t('label.autoPickIn', { s: secs })}
      </div>
    </div>
  );
}

function DraftCard(props: { opt: DraftOption; idx: number }) {
  const o = props.opt;
  let name = '';
  let desc = '';
  let type = '';
  let icon: preact.JSX.Element;
  let tags: string[] = [];
  let gains: string[] = [];
  if (o.type === 'keystone') {
    name = tn('keystone', o.id);
    desc = td('keystone', o.id);
    type = t('label.keystones');
    icon = <Icon kind="keystone" id={o.id} />;
    tags = KEYSTONES[o.id]?.synergy ?? [];
  } else if (o.type === 'blessing') {
    const def = BLESSINGS[o.id];
    name = tn('blessing', o.id);
    desc = td('blessing', o.id);
    type = `${t('label.blessing')} · ${t('label.rarity' + (def?.rarity ?? 0))}`;
    icon = <Icon kind="blessing" id={o.id} />;
    tags = def?.tags ?? [];
  } else {
    const def = SKILLS[o.id];
    const me = S.self.value;
    const slot = me ? [...me.actives, ...me.autos].find((x) => x.id === o.id) : undefined;
    name = tn('skill', o.id);
    desc = skillDesc(o.id, o.level, slot?.aspects);
    type = t('label.upgrade') + ` ${(o.level ?? 2) - 1}→${o.level}` + ' · ' + (def?.kind === 'active' ? t('label.actives') : def?.kind === 'auto' ? t('label.autos') : t('label.traits'));
    icon = <Icon kind="skill" id={o.id} level={o.level} />;
    tags = def?.tags ?? [];
    gains = gainLines(o.id, slot?.aspects, (o.level ?? 2) - 1, o.level ?? 2);
  }
  return (
    <div class={'dcard panel ' + o.type} onClick={() => game.input.pressPick(props.idx)}>
      <div class="row">
        {icon}
        <div class="col" style={{ gap: 0 }}>
          <span class="type">{type}</span>
          <span class="name">{name}</span>
        </div>
      </div>
      <div class="desc">{desc}</div>
      {gains.length > 0 && (
        <div class="gains">
          {gains.map((g) => (
            <div>{g}</div>
          ))}
        </div>
      )}
      <div class="row" style={{ justifyContent: 'space-between' }}>
        <span>
          {tags.slice(0, 3).map((tg) => (
            <span class="tag">{tg}</span>
          ))}
        </span>
        {!isTouchUi() && <span class="badge">{KEY_LABELS.picks[props.idx]}</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ build panel: the same editor as the menu

function ExitCast() {
  const lv = S.leaving.value;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!lv) return;
    const id = setInterval(() => tick((n) => n + 1), 50);
    return () => clearInterval(id);
  }, [lv]);
  if (!lv) return null;
  const pct = Math.min(100, ((performance.now() - lv.at) / Math.max(1, lv.sec * 1000)) * 100);
  return (
    <div class="exitcast panel">
      <div class="small">{t('label.exitCasting')}</div>
      <div class="bar thin">
        <div class="fill xp" style={{ width: pct + '%' }} />
      </div>
      <Btn small ghost onClick={() => game.cancelLeave()}>
        {t('btn.cancel')}
      </Btn>
    </div>
  );
}

function BuildPanel() {
  const me = S.self.value;
  const spec = S.spectate.value;
  const [tab, setTab] = useState<'kit' | 'gear' | 'stats'>('kit');
  if (!me) return null;
  const readonly = !!spec || me.dead;
  return (
    <div class="card buildpanel panel col">
      <div class="row">
        <h2>{t('label.build')}</h2>
        <div class="grow" />
        <Btn small onClick={() => (S.buildOpen.value = false)}>
          {t('btn.close')}
        </Btn>
      </div>
      <div class="tabs">
        {(['kit', 'gear', 'stats'] as const).map((k) => (
          <button class={'btn small' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>
            {t('label.tab.' + k)}
          </button>
        ))}
      </div>
      {tab === 'kit' && (
        <>
          <div class="small muted">{t('label.buildInRealmHint')}</div>
          <KitPanel
            kit={{
              heroId: me.hero,
              rank: me.rank,
              points: me.pts,
              actives: me.actives,
              autos: me.autos,
              traits: me.traits,
              blessings: me.bless,
              keystones: me.keystones,
              readonly,
              onOp: (op) => game.build(op),
            }}
            fame={S.account.value?.fame}
          />
        </>
      )}
      {tab === 'gear' && (
        <GearPanel
          gear={{
            heroId: me.hero,
            weapon: me.eq.weapon,
            armor: me.eq.armor,
            charm: me.eq.charm,
            backpack: me.bp,
            readonly,
            onEquip: (i) => {
              const it = me.bp[i];
              if (it) game.build({ op: 'equip', uid: it.uid });
            },
            onUnequip: (slot) => game.build({ op: 'unequip', slot }),
            onDrop: (i) => {
              const it = me.bp[i];
              if (it) game.build({ op: 'drop', uid: it.uid });
            },
          }}
        />
      )}
      {tab === 'stats' && (
        <>
          <h3>{t('label.stats')}</h3>
          <div class="kv">
            {Object.entries(me.stats).map(([k, v]) => (
              <>
                <span>{t('stat.' + k)}</span>
                <span>{typeof v === 'number' && (k === 'crit' || k === 'thorns' || k === 'ls') ? Math.round(v * 100) + '%' : k === 'dmg' || k === 'aspd' || k === 'cdr' || k === 'area' ? '×' + v : v}</span>
              </>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Feed() {
  const items = S.feed.value;
  if (!items.length) return null;
  return (
    <div class="feed">
      {items.map((f) => (
        <div key={f.id} style={{ color: f.color ?? '#cfc7b8' }}>
          {f.text}
        </div>
      ))}
    </div>
  );
}

function Chat() {
  const open = S.chatOpen.value;
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  const now = Date.now();
  const msgs = S.chat.value.filter((m) => open || now - m.at < 12000).slice(-8);
  return (
    <div class={'chat' + (open ? ' open' : '')}>
      <div class="msgs">
        {msgs.map((m) => (
          <div key={m.id}>
            {m.system ? (
              <span class="muted">{m.text}</span>
            ) : (
              <>
                <span class={m.spectator ? 'from spec' : 'from'}>
                  {m.spectator ? '👁 ' : ''}
                  {m.from}:
                </span>{' '}
                {m.text}
              </>
            )}
          </div>
        ))}
      </div>
      {open && (
        <div class="row chatline">
          <input
            ref={ref}
            class="text grow"
            maxLength={200}
            placeholder={isTouchUi() ? t('label.chatPlaceholderTouch') : t('label.chatPlaceholder')}
            value={text}
            onInput={(e) => setText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (text.trim()) game.chat(text);
                setText('');
                S.chatOpen.value = false;
              } else if (e.key === 'Escape') {
                S.chatOpen.value = false;
              }
              e.stopPropagation();
            }}
          />
          {isTouchUi() && (
            <>
              <Btn
                small
                primary
                onClick={() => {
                  if (text.trim()) game.chat(text);
                  setText('');
                  S.chatOpen.value = false;
                }}
              >
                {t('btn.send')}
              </Btn>
              <Btn small onClick={() => (S.chatOpen.value = false)}>
                ✕
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DeathCard() {
  const c = S.deathCard.value!;
  const unlocked = unlockedView();
  const [hero, setHero] = useState<HeroId>(unlocked.includes(S.heroChoice.value) ? S.heroChoice.value : 'revenant');
  const url = `${origin()}#/g/${c.ghostId}${S.account.value ? `?ref=${S.account.value.id}` : ''}`;
  const killer = c.killer.kind === 'player' ? c.killer.name : c.killer.kind.startsWith('revenant') ? `${t('label.revenant')} ${c.killer.name}` : tn('mob', c.killer.kind);
  return (
    <div class="modal">
      <div class="card wide panel col death">
        <h1 style={{ fontSize: 34 }}>{t('label.deathTitle')}</h1>
        <div class="muted">{t('label.deathSub', { name: c.name })}</div>
        <div class="stats">
          <div>
            <span class="muted small">{t('label.level')}</span>
            <b>{c.level}</b>
          </div>
          <div>
            <span class="muted small">{t('label.rankTrack')}</span>
            <b>{c.rank}</b>
          </div>
          <div>
            <span class="muted small">{t('label.kills')}</span>
            <b>{c.kills}</b>
          </div>
          <div>
            <span class="muted small">{t('label.survived')}</span>
            <b>{fmtTime(c.survivedSec)}</b>
          </div>
          <div>
            <span class="muted small">{t('label.killedBy')}</span>
            <b style={{ fontSize: 14 }}>{killer || '—'}</b>
          </div>
          <div>
            <span class="muted small">{t('label.fame')}</span>
            <b>+{c.fame}</b>
          </div>
        </div>
        {c.martyr && <div class="small" style={{ color: '#ff9ae8' }}>{t('label.martyr')}</div>}
        <div class="row wrap">
          <Icon kind="item" id={c.build.weapon} size="sm" title={tn('item', c.build.weapon)} />
          {c.build.actives.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" level={s.level} title={skillTitle(s.id, s.level, s.aspects)} />
          ))}
          {c.build.autos.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" level={s.level} title={skillTitle(s.id, s.level, s.aspects)} />
          ))}
          {c.build.traits.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" level={s.level} title={tn('skill', s.id)} dim />
          ))}
          {c.build.keystones.map((k) => (
            <Icon kind="keystone" id={k} size="sm" title={tn('keystone', k)} />
          ))}
        </div>
        <div class="row">
          <Btn
            onClick={() => {
              void copyText(t('label.shareText', { hero: tn('hero', c.heroId), level: c.level, kills: c.kills, url }));
              S.showToast(t('toast.copied'));
              void api.event('share', { kind: 'ghost' });
            }}
          >
            {t('label.shareDeath')}
          </Btn>
          <span class="small muted">{url}</span>
        </div>
        <div class="sep" />
        {S.gameMode.value === 'realm' && <div class="small muted">{t('label.slotFreed')}</div>}
        <div class="row wrap">
          {unlocked.map((h) => (
            <div class={'row clickable panel'} style={{ padding: 6, border: '1px solid', borderColor: hero === h ? 'var(--gold)' : 'var(--border)', borderRadius: 4, cursor: 'pointer' }} onClick={() => setHero(h)}>
              <HeroPortrait hero={h} size={24} />
              <span style={{ color: HEROES[h].accent }}>{tn('hero', h)}</span>
            </div>
          ))}
          <div class="grow" />
          <Btn
            primary
            onClick={() => {
              S.setHero(hero);
              game.respawn(hero);
            }}
          >
            {t('btn.respawn', { hero: tn('hero', hero) })}
          </Btn>
          <Btn onClick={() => game.leave()}>{t('btn.menu')}</Btn>
        </div>
      </div>
    </div>
  );
}

function Menu() {
  const spec = S.spectate.value;
  const touch = isTouchUi();
  // a thumb reads a list of gestures, a keyboard reads a list of keys
  const controls = touch ? (
    <div class="controls">
      <span>{t('ctl.move')}</span>
      <span>{t('ctl.v.leftHalf')}</span>
      <span>{t('ctl.aim')}</span>
      <span>{t('ctl.v.rightHalf')}</span>
      <span>{t('ctl.dash')}</span>
      <span>»</span>
      <span>{t('ctl.skills')}</span>
      <span>{t('ctl.v.skillBtns')}</span>
      <span>{t('ctl.cons')}</span>
      <span>{t('ctl.v.consRow')}</span>
      <span>{t('ctl.pick')}</span>
      <span>{t('ctl.v.tapCard')}</span>
      <span>{t('ctl.interact')}</span>
      <span>✋</span>
      <span>{t('ctl.autofire')}</span>
      <span>⚔</span>
      <span>{t('ctl.map')}</span>
      <span>⌖</span>
    </div>
  ) : (
    <div class="controls">
      <span>{t('ctl.move')}</span>
      <span>WASD</span>
      <span>{t('ctl.fire')}</span>
      <span>LMB</span>
      <span>{t('ctl.dash')}</span>
      <span>Space / Shift</span>
      <span>{t('ctl.skills')}</span>
      <span>Q E R F (RMB = 1)</span>
      <span>{t('ctl.cons')}</span>
      <span>1 2 3 4</span>
      <span>{t('ctl.pick')}</span>
      <span>Z X C V B</span>
      <span>{t('ctl.interact')}</span>
      <span>G</span>
      <span>{t('ctl.autofire')}</span>
      <span>I</span>
      <span>{t('ctl.map')}</span>
      <span>M</span>
    </div>
  );
  const leave = spec ? (
    <Btn danger onClick={() => game.unwatch()}>
      {t('btn.stopWatching')}
    </Btn>
  ) : (
    <Btn danger onClick={() => game.leave()}>
      {S.gameMode.value === 'realm' ? t('btn.leaveSave') : t('btn.leave')}
    </Btn>
  );
  return (
    <div class="modal">
      <div class="card narrow panel col">
        <h2>{t('ctl.menu')}</h2>
        <Btn onClick={() => (S.menuOpen.value = false)}>{t('btn.continue')}</Btn>
        <Btn onClick={() => (S.buildOpen.value = !S.buildOpen.value)}>{t('label.build')}</Btn>
        <div class="sep" />
        {/* on a phone every button must be reachable without scrolling past a reference list */}
        {touch ? (
          <>
            {leave}
            <div class="sep" />
            {controls}
          </>
        ) : (
          <>
            {controls}
            <div class="sep" />
            {leave}
          </>
        )}
      </div>
    </div>
  );
}
